import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import Draggable from 'react-draggable';
import { I } from '../../../common/v5/config';
import WorkflowToolbar from './components/WorkflowToolbar';
import BottomToolbar from './components/BottomToolbar';
import CentionHeader from './components/CentionHeader';
import CentionSidebar from './components/CentionSidebar';
import WorkflowSettings from './components/WorkflowSettings';
import DebugErrorView from './components/DebugErrorView';
import ToolForm from './components/ToolForm';
import AgentForm from './components/AgentForm';
import HelpTooltip from './components/HelpTooltip';
import './components/ToolForm.css';
import { useWorkflowSaving } from './hooks/useWorkflowSaving';
import { setUnsavedChanges, setCurrentWorkflow, patchAgentFolder } from '../../../redux/actions/async/agentBuilder';
import { UnnestedAreaSingleSelect } from '../../../reactcomponents/Dropdown';
import { AgentPrefix } from '../../../redux/constants/endpoints';
import { CheckEndpoint } from '../../../redux/actions/async/restful';
import './styles/cention-tokens.scss';

// CENTION Exact Constants
const GRID_SIZE = 16;
const DEFAULT_NODE_SIZE = [GRID_SIZE * 6, GRID_SIZE * 6];
const CONFIGURATION_NODE_RADIUS = (GRID_SIZE * 5) / 2;
const CONFIGURABLE_NODE_SIZE = [GRID_SIZE * 16, GRID_SIZE * 6];

// CENTION Exact Color System (from _primitives.scss and _tokens.scss)
const CENTION_COLORS = {
  // Primitive grays
  white: 'hsl(220, 50%, 100%)',
  gray010: 'hsl(220, 50%, 99%)',
  gray025: 'hsl(220, 50%, 97.5%)',
  gray030: 'hsl(220, 43%, 97%)',
  gray040: 'hsl(220, 40%, 96%)',
  gray070: 'hsl(220, 32%, 93%)',
  gray120: 'hsl(220, 25%, 88%)',
  gray200: 'hsl(220, 18%, 80%)',
  gray320: 'hsl(220, 10%, 68%)',
  gray420: 'hsl(220, 4%, 58%)',
  gray490: 'hsl(220, 3%, 51%)',
  gray540: 'hsl(220, 4%, 46%)',
  gray670: 'hsl(220, 2%, 33%)',
  gray740: 'hsl(220, 2%, 26%)',
  gray780: 'hsl(220, 1%, 22%)',
  gray800: 'hsl(220, 1%, 20%)',
  gray820: 'hsl(220, 1%, 18%)',

  // Primary colors (Cention's signature coral/orange)
  primary030: 'hsl(7, 100%, 98%)',
  primary050: 'hsl(7, 100%, 95%)',
  primary070: 'hsl(7, 100%, 93%)',
  primary120: 'hsl(7, 100%, 88%)',
  primary220: 'hsl(7, 100%, 78%)',
  primary320: 'hsl(7, 100%, 68%)', // Base primary
  primary420: 'hsl(7, 85%, 58%)',

  // Secondary colors (purple)
  secondary070: 'hsl(247, 49%, 93%)',
  secondary170: 'hsl(247, 49%, 83%)',
  secondary270: 'hsl(247, 49%, 73%)',
  secondary370: 'hsl(247, 49%, 63%)',
  secondary470: 'hsl(247, 49%, 53%)', // Base secondary
  secondary570: 'hsl(247, 49%, 43%)',
  secondary720: 'hsl(247, 24%, 28%)',

  // Success colors (green)
  success050: 'hsl(150, 60%, 95%)',
  success100: 'hsl(150, 60%, 90%)',
  success200: 'hsl(150, 60%, 80%)',
  success300: 'hsl(150, 60%, 70%)',
  success600: 'hsl(150, 60%, 40%)', // Base success
  success700: 'hsl(150, 53%, 30%)',
  success800: 'hsl(150, 45%, 20%)',

  // Warning colors (yellow/orange)
  warning030: 'hsl(36, 77%, 97%)',
  warning130: 'hsl(36, 77%, 87%)',
  warning180: 'hsl(36, 77%, 82%)',
  warning280: 'hsl(36, 77%, 72%)',
  warning430: 'hsl(36, 77%, 57%)', // Base warning
  warning530: 'hsl(36, 77%, 47%)',
  warning680: 'hsl(36, 42%, 32%)',
  warning780: 'hsl(36, 42%, 22%)',

  // Danger colors (red)
  danger030: 'hsl(355, 83%, 97%)',
  danger080: 'hsl(355, 83%, 92%)',
  danger180: 'hsl(355, 83%, 82%)',
  danger230: 'hsl(355, 83%, 77%)',
  danger330: 'hsl(355, 83%, 67%)',
  danger480: 'hsl(355, 83%, 52%)', // Base danger
  danger580: 'hsl(355, 83%, 42%)',
  danger630: 'hsl(355, 53%, 37%)',
  danger730: 'hsl(355, 43%, 27%)',

  // Sticky note colors
  stickyYellow080: 'hsl(46, 100%, 92%)',
  stickyYellow230: 'hsl(46, 100%, 77%)',

  // Alpha variants
  primaryAlpha010: 'hsla(7, 100%, 68%, 0.1)',
  primaryAlpha035: 'hsla(7, 100%, 68%, 0.35)',
  primaryAlpha050: 'hsla(7, 100%, 68%, 0.5)',
  whiteAlpha075: 'hsla(220, 50%, 100%, 0.75)',
  grayAlpha010: 'hsla(220, 10%, 68%, 0.1)',
};

// CENTION Design Tokens (mapped from primitives)
const CENTION_TOKENS = {
  // Text colors
  textDark: CENTION_COLORS.gray740,
  textBase: CENTION_COLORS.gray540,
  textLight: CENTION_COLORS.gray420,
  textLighter: CENTION_COLORS.gray120,
  textXlight: CENTION_COLORS.white,
  textDanger: CENTION_COLORS.danger480,

  // Foreground colors
  foregroundXdark: CENTION_COLORS.gray490,
  foregroundDark: CENTION_COLORS.gray200,
  foregroundBase: CENTION_COLORS.gray120,
  foregroundLight: CENTION_COLORS.gray070,
  foregroundXlight: CENTION_COLORS.white,

  // Background colors
  backgroundDark: CENTION_COLORS.gray820,
  backgroundMedium: CENTION_COLORS.gray120,
  backgroundBase: CENTION_COLORS.gray040,
  backgroundLightBase: CENTION_COLORS.gray025,
  backgroundLight: CENTION_COLORS.gray010,
  backgroundXlight: CENTION_COLORS.white,

  // Canvas colors
  canvasBackground: CENTION_COLORS.gray010,
  canvasDot: CENTION_COLORS.gray120,
  canvasSelected: CENTION_COLORS.gray070,
  canvasSelectedTransparent: 'hsla(220, 47%, 30%, 0.1)',
  canvasLabelBackground: 'hsla(220, 47%, 99%, 0.85)',

  // Node colors
  nodeBackground: CENTION_COLORS.white,
  nodeExecutingBackground: CENTION_COLORS.primary030,
  nodePinnedBorder: CENTION_COLORS.secondary470,
  nodeRunningBorder: CENTION_COLORS.primary320,

  // Primary color variants
  primary: CENTION_COLORS.primary320,
  primaryShade1: CENTION_COLORS.primary420,
  primaryTint1: CENTION_COLORS.primary120,
  primaryTint2: CENTION_COLORS.primary070,
  primaryTint3: CENTION_COLORS.primary030,

  // Secondary color variants
  secondary: CENTION_COLORS.secondary470,
  secondaryShade1: CENTION_COLORS.secondary570,
  secondaryTint1: CENTION_COLORS.secondary170,
  secondaryTint3: CENTION_COLORS.secondary070,

  // Success color variants
  success: CENTION_COLORS.success600,
  successShade1: CENTION_COLORS.success700,
  successLight: CENTION_COLORS.success300,
  successLight2: CENTION_COLORS.success200,
  successTint1: CENTION_COLORS.success100,
  successTint2: CENTION_COLORS.success050,

  // Warning color variants
  warning: CENTION_COLORS.warning430,
  warningShade1: CENTION_COLORS.warning530,
  warningTint1: CENTION_COLORS.warning180,
  warningTint2: CENTION_COLORS.warning030,

  // Danger color variants
  danger: CENTION_COLORS.danger480,
  dangerShade1: CENTION_COLORS.danger580,
  dangerLight: CENTION_COLORS.danger330,
  dangerLight2: CENTION_COLORS.danger230,
  dangerTint1: CENTION_COLORS.danger080,
  dangerTint2: CENTION_COLORS.danger030,

  // Box shadows
  boxShadowBase: '0 2px 4px rgba(0, 0, 0, 0.12), 0 0 6px rgba(0, 0, 0, 0.04)',
  boxShadowDark: '0 2px 4px rgba(0, 0, 0, 0.12), 0 0 6px rgba(0, 0, 0, 0.12)',
  boxShadowLight: '0 2px 12px 0 rgba(0, 0, 0, 0.07)',

  // Border radius
  borderRadiusXlarge: '12px',
  borderRadiusLarge: '8px',
  borderRadiusBase: '4px',
  borderRadiusSmall: '2px',

  // Font sizes
  fontSize4xs: '0.5rem',    // 8px
  fontSize3xs: '0.625rem',  // 10px
  fontSize2xs: '0.75rem',   // 12px
  fontSizeXs: '0.8125rem',  // 13px
  fontSizeS: '0.875rem',    // 14px
  fontSizeM: '1rem',        // 16px
  fontSizeL: '1.125rem',    // 18px
  fontSizeXl: '1.25rem',    // 20px
  fontSize2xl: '1.75rem',   // 28px

  // Font weights
  fontWeightRegular: '400',
  fontWeightMedium: '500',
  fontWeightBold: '600',

  // Line heights
  lineHeightXsmall: '1',
  lineHeightCompact: '1.25',
  lineHeightRegular: '1.3',
  lineHeightLoose: '1.35',
  lineHeightXloose: '1.5',

  // Spacing
  spacing5xs: '0.125rem',   // 2px
  spacing4xs: '0.25rem',    // 4px
  spacing3xs: '0.375rem',   // 6px
  spacing2xs: '0.5rem',     // 8px
  spacingXs: '0.75rem',     // 12px
  spacingS: '1rem',         // 16px
  spacingM: '1.25rem',      // 20px
  spacingL: '1.5rem',       // 24px
  spacingXl: '2rem',        // 32px
  spacing2xl: '3rem',       // 48px
  spacing3xl: '4rem',       // 64px
};

// CENTION Canvas Container with exact styling
const CanvasContainer = styled.div`
  width: 100%;
  height: calc(100vh - 60px); /* Account for header height */
  background: ${CENTION_TOKENS.canvasBackground};
  position: relative;
  overflow: hidden;
  cursor: grab;
  font-family: InterVariable, sans-serif;

  &.dragging {
    cursor: grabbing;
  }

  &.selection {
    cursor: default;
  }

  * {
    box-sizing: border-box;
  }
`;

// CENTION Background with exact dot pattern
const CanvasBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: radial-gradient(circle, ${CENTION_TOKENS.canvasDot} 1px, transparent 1px);
  background-size: ${GRID_SIZE}px ${GRID_SIZE}px;
  background-position: 0 0;
  pointer-events: none;
  z-index: 0;
`;

// CENTION Sidebar with exact styling
const Sidebar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 385px;
  height: 100%;
  background: ${CENTION_TOKENS.backgroundXlight};
  border-right: 1px solid ${CENTION_TOKENS.foregroundBase};
  z-index: 10;
  overflow-y: auto;
  padding: ${CENTION_TOKENS.spacingS};
  box-shadow: ${CENTION_TOKENS.boxShadowLight};
`;

const SidebarHeader = styled.div`
  padding: ${CENTION_TOKENS.spacingS} 0;
  border-bottom: 1px solid ${CENTION_TOKENS.foregroundLight};
  margin-bottom: ${CENTION_TOKENS.spacingM};
`;

const SidebarTitle = styled.h1`
  color: ${CENTION_TOKENS.textDark};
  font-size: ${CENTION_TOKENS.fontSizeL};
  font-weight: ${CENTION_TOKENS.fontWeightBold};
  margin: 0 0 ${CENTION_TOKENS.spacing2xs} 0;
  line-height: ${CENTION_TOKENS.lineHeightCompact};
`;

const SidebarSubtitle = styled.p`
  color: ${CENTION_TOKENS.textLight};
  font-size: ${CENTION_TOKENS.fontSize2xs};
  margin: 0;
  line-height: ${CENTION_TOKENS.lineHeightRegular};
`;

const SidebarSection = styled.div`
  margin-bottom: ${CENTION_TOKENS.spacingXl};
`;

const SectionTitle = styled.h3`
  color: ${CENTION_TOKENS.textDark};
  font-size: ${CENTION_TOKENS.fontSizeS};
  font-weight: ${CENTION_TOKENS.fontWeightMedium};
  margin: 0 0 ${CENTION_TOKENS.spacingXs} 0;
  padding-bottom: ${CENTION_TOKENS.spacing2xs};
  border-bottom: 1px solid ${CENTION_TOKENS.foregroundLight};
  line-height: ${CENTION_TOKENS.lineHeightCompact};
`;

// CENTION Draggable Item with exact styling
const DraggableItem = styled.div`
  background: ${CENTION_TOKENS.backgroundXlight};
  border: 1px solid ${CENTION_TOKENS.foregroundBase};
  border-radius: ${CENTION_TOKENS.borderRadiusLarge};
  padding: ${CENTION_TOKENS.spacingXs};
  margin-bottom: ${CENTION_TOKENS.spacing2xs};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${CENTION_TOKENS.spacingXs};
  position: relative;

  &:hover {
    border-color: ${CENTION_TOKENS.primary};
    box-shadow: ${CENTION_TOKENS.boxShadowBase};
    transform: translateY(-1px);

    &::after {
      content: "➕";
      position: absolute;
      right: ${CENTION_TOKENS.spacingXs};
      top: 50%;
      transform: translateY(-50%);
      color: ${CENTION_TOKENS.primary};
      font-size: ${CENTION_TOKENS.fontSizeS};
      font-weight: ${CENTION_TOKENS.fontWeightBold};
    }
  }

  &:active {
    transform: translateY(0);
    box-shadow: ${CENTION_TOKENS.boxShadowDark};
  }
`;

const ItemIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: ${CENTION_TOKENS.borderRadiusBase};
  background: ${CENTION_TOKENS.backgroundLight};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${CENTION_TOKENS.fontSizeS};
  color: ${CENTION_TOKENS.textBase};
  flex-shrink: 0;
  border: 1px solid ${CENTION_TOKENS.foregroundLight};
`;

const ItemContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemTitle = styled.div`
  font-size: ${CENTION_TOKENS.fontSizeS};
  font-weight: ${CENTION_TOKENS.fontWeightMedium};
  color: ${CENTION_TOKENS.textDark};
  margin-bottom: ${CENTION_TOKENS.spacing4xs};
  line-height: ${CENTION_TOKENS.lineHeightCompact};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemDescription = styled.div`
  font-size: ${CENTION_TOKENS.fontSize2xs};
  color: ${CENTION_TOKENS.textLight};
  line-height: ${CENTION_TOKENS.lineHeightRegular};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// CENTION Workflow Canvas
const WorkflowCanvas = styled.div`
  position: absolute;
  top: 0;
  left: 385px;
  right: 0;
  bottom: 0;
  background: ${CENTION_TOKENS.canvasBackground};
  overflow: hidden;
`;

// CENTION Node with exact styling from CanvasNodeDefault.vue
const WorkflowNode = styled.div`
  --canvas-node-border-width: 2px;
  --trigger-node-border-radius: 36px;
  --canvas-node-status-icons-offset: ${CENTION_TOKENS.spacing3xs};
  --node-icon-color: ${CENTION_TOKENS.foregroundDark};

  position: absolute;
  height: ${props => props.height || DEFAULT_NODE_SIZE[1]}px;
  width: ${props => props.width || DEFAULT_NODE_SIZE[0]}px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${CENTION_TOKENS.nodeBackground};
  border: var(--canvas-node-border-width) solid ${CENTION_TOKENS.foregroundBase};
  border-radius: ${CENTION_TOKENS.borderRadiusLarge};
  cursor: grab;
  transition: all 0.2s ease;
  user-select: none;

  &.trigger {
    border-radius: var(--trigger-node-border-radius) ${CENTION_TOKENS.borderRadiusLarge}
                   ${CENTION_TOKENS.borderRadiusLarge} var(--trigger-node-border-radius);
  }

  &.configuration {
    background: ${CENTION_TOKENS.backgroundLight};
    border-radius: calc(var(--canvas-node-height) / 2);
  }

  &.selected {
    box-shadow: 0 0 0 calc(8px * 1) ${CENTION_TOKENS.canvasSelectedTransparent};
  }

  &.success {
    border-color: ${CENTION_TOKENS.success};
  }

  &.warning {
    border-color: ${CENTION_TOKENS.warning};
  }

  &.error {
    border-color: ${CENTION_TOKENS.danger};
  }

  &.pinned {
    border-color: ${CENTION_TOKENS.nodePinnedBorder};
  }

  &.disabled {
    border-color: ${CENTION_TOKENS.foregroundBase};
  }

  &.running {
    background-color: ${CENTION_TOKENS.nodeExecutingBackground};
    border-color: ${CENTION_TOKENS.nodeRunningBorder};
  }

  &.waiting {
    border-color: ${CENTION_TOKENS.secondary};
  }

  &:hover {
    box-shadow: ${CENTION_TOKENS.boxShadowBase};
  }

  &:active {
    cursor: grabbing;
  }
`;

const NodeIcon = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: var(--node-icon-color);
  flex-shrink: 0;
`;

const NodeDescription = styled.div`
  position: absolute;
  top: 100%;
  width: 100%;
  min-width: calc(${DEFAULT_NODE_SIZE[0]}px * 2);
  margin-top: ${CENTION_TOKENS.spacing2xs};
  display: flex;
  flex-direction: column;
  gap: ${CENTION_TOKENS.spacing4xs};
  pointer-events: none;
`;

const NodeLabel = styled.div`
  font-size: ${CENTION_TOKENS.fontSizeM};
  text-align: center;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  overflow-wrap: anywhere;
  font-weight: ${CENTION_TOKENS.fontWeightMedium};
  line-height: ${CENTION_TOKENS.lineHeightCompact};
  color: ${CENTION_TOKENS.textDark};
`;

const NodeSubtitle = styled.div`
  width: 100%;
  text-align: center;
  color: ${CENTION_TOKENS.textLight};
  font-size: ${CENTION_TOKENS.fontSizeXs};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: ${CENTION_TOKENS.lineHeightCompact};
  font-weight: ${CENTION_TOKENS.fontWeightRegular};
`;

// CENTION Connection Handles with exact styling
const ConnectionHandle = styled.div`
  --handle-indicator-width: calc(16px * 1);
  --handle-indicator-height: calc(16px * 1);

  position: absolute;
  width: var(--handle-indicator-width);
  height: var(--handle-indicator-height);
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border: 0;
  z-index: 1;
  background: transparent;
  border-radius: 0;
  cursor: crosshair;

  &.input {
    &.main {
      --handle-indicator-width: calc(8px * 1);
    }
  }

  &.input {
    left: calc(-1 * var(--handle-indicator-width) / 2);
    top: 50%;
    transform: translateY(-50%);
  }

  &.output {
    right: calc(-1 * var(--handle-indicator-width) / 2);
    top: 50%;
    transform: translateY(-50%);
  }
`;

// CENTION Handle Dot Component
const HandleDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${CENTION_TOKENS.backgroundXlight};
  border: 2px solid ${CENTION_TOKENS.foregroundBase};
  transition: all 0.2s ease;

  ${ConnectionHandle}:hover & {
    border-color: ${CENTION_TOKENS.primary};
    background: ${CENTION_TOKENS.primary};
    transform: scale(1.2);
  }

  &.connected {
    background: ${CENTION_TOKENS.success};
    border-color: ${CENTION_TOKENS.success};
  }
`;

// CENTION Connection Lines
const ConnectionLine = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
`;

// CENTION Execution Flow Particle Animation
const ExecutionParticle = styled.circle`
  r: 3;
  fill: ${CENTION_TOKENS.success};
  opacity: 0.8;

  &.running {
    animation: particle-flow 2s ease-in-out infinite;
  }

  @keyframes particle-flow {
    0% {
      opacity: 0;
      r: 2;
    }
    50% {
      opacity: 1;
      r: 4;
    }
    100% {
      opacity: 0;
      r: 2;
    }
  }
`;

// CENTION Connection Status Animations
const ConnectionStatusLine = styled.path`
  stroke-width: 3;
  fill: none;
  stroke-linecap: square;
  transition: all 0.3s ease;

  &.success {
    stroke: ${CENTION_TOKENS.success};
    stroke-dasharray: 8, 4;
    animation: success-flow 1.5s ease-in-out;
  }

  &.running {
    stroke: ${CENTION_TOKENS.warning};
    stroke-dasharray: 8, 4;
    animation: running-flow 2s linear infinite;
  }

  &.error {
    stroke: ${CENTION_TOKENS.danger};
    stroke-dasharray: 4, 4;
    animation: error-pulse 1s ease-in-out 3;
  }

  @keyframes success-flow {
    0% {
      stroke-dashoffset: 0;
      opacity: 0.5;
    }
    50% {
      opacity: 1;
    }
    100% {
      stroke-dashoffset: -24;
      opacity: 0.8;
    }
  }

  @keyframes running-flow {
    0% {
      stroke-dashoffset: 0;
    }
    100% {
      stroke-dashoffset: -24;
    }
  }

  @keyframes error-pulse {
    0%, 100% {
      opacity: 0.5;
    }
    50% {
      opacity: 1;
    }
  }
`;

// CENTION Node Status Indicator
const NodeStatusIndicator = styled.div`
  position: absolute;
  top: -4px;
  right: -4px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid ${CENTION_TOKENS.nodeBackground};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  z-index: 10;

  &.waiting {
    background: ${CENTION_TOKENS.secondary};
    animation: waiting-pulse 2s ease-in-out infinite;
  }

  &.running {
    background: ${CENTION_TOKENS.warning};
    animation: running-spin 1s linear infinite;
  }

  &.success {
    background: ${CENTION_TOKENS.success};
    color: white;
    animation: success-bounce 0.5s ease-out;
  }

  &.error {
    background: ${CENTION_TOKENS.danger};
    color: white;
    animation: error-shake 0.5s ease-out;
  }

  @keyframes waiting-pulse {
    0%, 100% {
      opacity: 0.6;
      transform: scale(1);
    }
    50% {
      opacity: 1;
      transform: scale(1.1);
    }
  }

  @keyframes running-spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  @keyframes success-bounce {
    0% {
      transform: scale(0.8);
    }
    50% {
      transform: scale(1.2);
    }
    100% {
      transform: scale(1);
    }
  }

  @keyframes error-shake {
    0%, 100% {
      transform: translateX(0);
    }
    25% {
      transform: translateX(-2px);
    }
    75% {
      transform: translateX(2px);
    }
  }
`;

// CENTION Bezier Path Generation (ported from @vue-flow/core)
const getBezierPath = ({ sourceX, sourceY, sourcePosition = 'right', targetX, targetY, targetPosition = 'left', curvature = 0.25 }) => {
  const [sourceControlX, sourceControlY] = getControlWithCurvature({
    pos: sourcePosition,
    x1: sourceX,
    y1: sourceY,
    x2: targetX,
    y2: targetY,
    c: curvature,
  });

  const [targetControlX, targetControlY] = getControlWithCurvature({
    pos: targetPosition,
    x1: targetX,
    y1: targetY,
    x2: sourceX,
    y2: sourceY,
    c: curvature,
  });

  const path = `M${sourceX},${sourceY} C${sourceControlX},${sourceControlY} ${targetControlX},${targetControlY} ${targetX},${targetY}`;

  // Calculate label position (midpoint of curve)
  const labelX = (sourceX + targetX) / 2;
  const labelY = (sourceY + targetY) / 2;

  return [path, labelX, labelY];
};

const getControlWithCurvature = ({ pos, x1, y1, x2, y2, c }) => {
  let curvature = c;

  switch (pos) {
    case 'left':
      return [x1 - curvature * Math.abs(x1 - x2), y1];
    case 'right':
      return [x1 + curvature * Math.abs(x1 - x2), y1];
    case 'top':
      return [x1, y1 - curvature * Math.abs(y1 - y2)];
    case 'bottom':
      return [x1, y1 + curvature * Math.abs(y1 - y2)];
  }
};

// CENTION Smooth Step Path for backwards connections
const getSmoothStepPath = ({ sourceX, sourceY, sourcePosition = 'right', targetX, targetY, targetPosition = 'left', borderRadius = 5, offset = 20 }) => {
  const [sourceControlX, sourceControlY] = getControlWithCurvature({
    pos: sourcePosition,
    x1: sourceX,
    y1: sourceY,
    x2: targetX,
    y2: targetY,
    c: 0.5,
  });

  const [targetControlX, targetControlY] = getControlWithCurvature({
    pos: targetPosition,
    x1: targetX,
    y1: targetY,
    x2: sourceX,
    y2: sourceY,
    c: 0.5,
  });

  const centerX = (sourceControlX + targetControlX) / 2;
  const centerY = (sourceControlY + targetControlY) / 2;

  const path = `M${sourceX},${sourceY} C${sourceControlX},${sourceControlY} ${centerX},${centerY} ${centerX},${centerY} C${centerX},${centerY} ${targetControlX},${targetControlY} ${targetX},${targetY}`;

  return [path, centerX, centerY];
};

// CENTION Edge Render Data
const getEdgeRenderData = (props, { connectionType = 'main' } = {}) => {
  const { targetX, targetY, sourceX, sourceY, sourcePosition = 'right', targetPosition = 'left' } = props;
  const isConnectorStraight = sourceY === targetY;
  const HANDLE_SIZE = 20;
  const EDGE_PADDING_BOTTOM = 130;
  const EDGE_PADDING_X = 40;
  const EDGE_BORDER_RADIUS = 16;

  const isRightOfSourceHandle = (sourceX, targetX) => sourceX - HANDLE_SIZE > targetX;

  if (!isRightOfSourceHandle(sourceX, targetX) || connectionType !== 'main') {
    const segment = getBezierPath(props);
    return {
      segments: [segment],
      labelPosition: [segment[1], segment[2]],
      isConnectorStraight,
    };
  }

  // Connection is backwards and the source is on the right side
  // -> We need to avoid overlapping the source node
  const firstSegmentTargetX = (sourceX + targetX) / 2;
  const firstSegmentTargetY = sourceY + EDGE_PADDING_BOTTOM;
  const firstSegment = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX: firstSegmentTargetX,
    targetY: firstSegmentTargetY,
    sourcePosition,
    targetPosition: 'right',
    borderRadius: EDGE_BORDER_RADIUS,
    offset: EDGE_PADDING_X,
  });

  const secondSegment = getSmoothStepPath({
    sourceX: firstSegmentTargetX,
    sourceY: firstSegmentTargetY,
    targetX,
    targetY,
    sourcePosition: 'left',
    targetPosition,
    borderRadius: EDGE_BORDER_RADIUS,
    offset: EDGE_PADDING_X,
  });

  return {
    segments: [firstSegment, secondSegment],
    labelPosition: [firstSegmentTargetX, firstSegmentTargetY],
    isConnectorStraight,
  };
};

// CENTION Connection Renderer Component with execution flow animations
const ConnectionRenderer = ({ connections, tempConnection, onDelete, onHover, hoveredConnection, executionState = {} }) => {
  const renderConnection = (connection, isTemp = false) => {
    const renderData = getEdgeRenderData({
      sourceX: connection.sourceX,
      sourceY: connection.sourceY,
      targetX: connection.targetX,
      targetY: connection.targetY,
      sourcePosition: 'right',
      targetPosition: 'left',
    }, {
      connectionType: connection.type || 'main'
    });

    const segments = renderData.segments;
    const [labelX, labelY] = renderData.labelPosition;
    const isHovered = hoveredConnection === connection.id;
    const connectionStatus = executionState[connection.id]?.status;

    return (
      <g key={connection.id || 'temp'}>
        {segments.map((segment, index) => {
          const segmentPath = segment[0];
          const segmentKey = `${connection.id || 'temp'}-${index}`;

          return (
            <g key={segmentKey}>
              {/* Base connection line */}
              <path
                d={segmentPath}
                stroke={isTemp ? CENTION_TOKENS.primary : (isHovered ? CENTION_TOKENS.primary : CENTION_TOKENS.foregroundXdark)}
                strokeWidth={isTemp ? 3 : 2}
                fill="none"
                strokeDasharray={isTemp ? "5,5" : "none"}
                opacity={isTemp ? 0.7 : 1}
                strokeLinecap="square"
                onMouseEnter={() => !isTemp && onHover && onHover(connection.id)}
                onMouseLeave={() => !isTemp && onHover && onHover(null)}
                style={{
                  cursor: isTemp ? 'default' : 'pointer',
                  transition: 'stroke 0.3s ease'
                }}
              />

              {/* Execution status overlay */}
              {!isTemp && connectionStatus && (
                <ConnectionStatusLine
                  d={segmentPath}
                  className={connectionStatus}
                />
              )}

              {/* Moving execution particle */}
              {!isTemp && connectionStatus === 'running' && (
                <ExecutionParticle
                  className="running"
                >
                  <animateMotion
                    dur="2s"
                    repeatCount="indefinite"
                    path={segmentPath}
                  />
                </ExecutionParticle>
              )}
            </g>
          );
        })}

        {/* Connection arrowhead */}
        {!isTemp && (
          <polygon
            points={`${connection.targetX - 5},${connection.targetY - 3} ${connection.targetX},${connection.targetY} ${connection.targetX - 5},${connection.targetY + 3}`}
            fill={
              connectionStatus === 'success' ? CENTION_TOKENS.success :
                connectionStatus === 'error' ? CENTION_TOKENS.danger :
                  connectionStatus === 'running' ? CENTION_TOKENS.warning :
                    (isHovered ? CENTION_TOKENS.primary : CENTION_TOKENS.foregroundXdark)
            }
            style={{ transition: 'fill 0.3s ease' }}
          />
        )}

        {/* Connection Toolbar */}
        {!isTemp && isHovered && onDelete && (
          <foreignObject
            x={labelX - 30}
            y={labelY - 15}
            width={60}
            height={30}
          >
            <ConnectionToolbar>
              <ToolbarButton
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(connection.id);
                }}
                title="Delete connection"
              >
                ×
              </ToolbarButton>
            </ConnectionToolbar>
          </foreignObject>
        )}
      </g>
    );
  };

  return (
    <>
      {connections.map(conn => renderConnection(conn, false))}
      {tempConnection && renderConnection(tempConnection, true)}
    </>
  );
};

// CENTION Toolbar Components with exact styling
const ConnectionToolbar = styled.div`
  position: absolute;
  background: ${CENTION_TOKENS.backgroundXlight};
  border: 1px solid ${CENTION_TOKENS.foregroundBase};
  border-radius: ${CENTION_TOKENS.borderRadiusLarge};
  padding: ${CENTION_TOKENS.spacing4xs};
  display: flex;
  gap: ${CENTION_TOKENS.spacing4xs};
  box-shadow: ${CENTION_TOKENS.boxShadowBase};
  z-index: 30;
  transform: translate(-50%, -50%);
`;

const ToolbarButton = styled.button`
  width: 24px;
  height: 24px;
  border-radius: ${CENTION_TOKENS.borderRadiusBase};
  border: none;
  background: ${CENTION_TOKENS.backgroundLight};
  color: ${CENTION_TOKENS.textDark};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${CENTION_TOKENS.fontSize2xs};
  font-weight: ${CENTION_TOKENS.fontWeightMedium};
  transition: all 0.2s ease;

  &:hover {
    background: ${CENTION_TOKENS.danger};
    color: ${CENTION_TOKENS.textXlight};
  }

  &:active {
    transform: scale(0.95);
  }
`;

// CENTION Context Menu with exact styling
const ContextMenu = styled.div`
  position: fixed;
  background: ${CENTION_TOKENS.backgroundXlight};
  border: 1px solid ${CENTION_TOKENS.foregroundBase};
  border-radius: ${CENTION_TOKENS.borderRadiusLarge};
  padding: ${CENTION_TOKENS.spacing2xs} 0;
  box-shadow: ${CENTION_TOKENS.boxShadowBase};
  z-index: 1000;
  min-width: 150px;
`;

const ContextMenuItem = styled.div`
  padding: ${CENTION_TOKENS.spacing2xs} ${CENTION_TOKENS.spacingS};
  cursor: pointer;
  font-size: ${CENTION_TOKENS.fontSizeS};
  color: ${CENTION_TOKENS.textDark};
  transition: background-color 0.2s ease;
  line-height: ${CENTION_TOKENS.lineHeightCompact};

  &:hover {
    background: ${CENTION_TOKENS.backgroundLight};
  }

  &.danger {
    color: ${CENTION_TOKENS.danger};

    &:hover {
      background: ${CENTION_TOKENS.danger};
      color: ${CENTION_TOKENS.textXlight};
    }
  }
`;

// CENTION Execution Controls
const ExecutionControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${CENTION_TOKENS.spacingXs};
  margin-bottom: ${CENTION_TOKENS.spacingS};
`;

const ExecutionStatus = styled.div`
  background: ${CENTION_TOKENS.backgroundLight};
  padding: ${CENTION_TOKENS.spacingXs};
  border-radius: ${CENTION_TOKENS.borderRadiusBase};
  font-size: ${CENTION_TOKENS.fontSizeXs};
  color: ${CENTION_TOKENS.foregroundDark};
  text-align: center;

  div {
    margin: ${CENTION_TOKENS.spacing3xs} 0;
  }

  div:first-child {
    font-weight: 600;
    color: ${CENTION_TOKENS.success};
  }
`;

// CENTION Modal Components with exact styling
const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${CENTION_TOKENS.whiteAlpha075};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${CENTION_TOKENS.backgroundXlight};
  border: 1px solid ${CENTION_TOKENS.foregroundBase};
  border-radius: ${CENTION_TOKENS.borderRadiusXlarge};
  padding: ${CENTION_TOKENS.spacingXl};
  min-width: 500px;
  max-width: 800px;
  width: 90vw;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: ${CENTION_TOKENS.boxShadowDark};
`;

const ModalTitle = styled.h2`
  color: ${CENTION_TOKENS.textDark};
  margin: 0 0 ${CENTION_TOKENS.spacingM} 0;
  font-size: ${CENTION_TOKENS.fontSizeXl};
  font-weight: ${CENTION_TOKENS.fontWeightBold};
  line-height: ${CENTION_TOKENS.lineHeightCompact};
`;

const FormGroup = styled.div`
  margin-bottom: ${CENTION_TOKENS.spacingM};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${CENTION_TOKENS.spacing2xs};
  color: ${CENTION_TOKENS.textDark};
  font-weight: ${CENTION_TOKENS.fontWeightMedium};
  font-size: ${CENTION_TOKENS.fontSizeS};
  line-height: ${CENTION_TOKENS.lineHeightCompact};
`;

const Input = styled.input`
  width: 100%;
  padding: ${CENTION_TOKENS.spacingXs} ${CENTION_TOKENS.spacingS};
  background: ${CENTION_TOKENS.backgroundXlight};
  border: 1px solid ${CENTION_TOKENS.foregroundBase};
  border-radius: ${CENTION_TOKENS.borderRadiusBase};
  font-size: ${CENTION_TOKENS.fontSizeS};
  color: ${CENTION_TOKENS.textDark};
  transition: all 0.2s ease;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${CENTION_TOKENS.primary};
    box-shadow: 0 0 0 3px ${CENTION_TOKENS.primaryAlpha010};
  }

  &::placeholder {
    color: ${CENTION_TOKENS.textLight};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${CENTION_TOKENS.spacingXs} ${CENTION_TOKENS.spacingS};
  background: ${CENTION_TOKENS.backgroundXlight};
  border: 1px solid ${CENTION_TOKENS.foregroundBase};
  border-radius: ${CENTION_TOKENS.borderRadiusBase};
  font-size: ${CENTION_TOKENS.fontSizeS};
  color: ${CENTION_TOKENS.textDark};
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  transition: all 0.2s ease;
  line-height: ${CENTION_TOKENS.lineHeightRegular};

  &:focus {
    outline: none;
    border-color: ${CENTION_TOKENS.primary};
    box-shadow: 0 0 0 3px ${CENTION_TOKENS.primaryAlpha010};
  }

  &::placeholder {
    color: ${CENTION_TOKENS.textLight};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: ${CENTION_TOKENS.spacingXs} ${CENTION_TOKENS.spacingS};
  background: ${CENTION_TOKENS.backgroundXlight};
  border: 1px solid ${CENTION_TOKENS.foregroundBase};
  border-radius: ${CENTION_TOKENS.borderRadiusBase};
  font-size: ${CENTION_TOKENS.fontSizeS};
  color: ${CENTION_TOKENS.textDark};
  transition: all 0.2s ease;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${CENTION_TOKENS.primary};
    box-shadow: 0 0 0 3px ${CENTION_TOKENS.primaryAlpha010};
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${CENTION_TOKENS.spacingXs};
  justify-content: flex-end;
  margin-top: ${CENTION_TOKENS.spacingXl};
`;

// CENTION Button with exact styling
const Button = styled.button`
  padding: ${CENTION_TOKENS.spacingXs} ${CENTION_TOKENS.spacingM};
  border-radius: ${CENTION_TOKENS.borderRadiusBase};
  font-size: ${CENTION_TOKENS.fontSizeS};
  font-weight: ${CENTION_TOKENS.fontWeightMedium};
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${CENTION_TOKENS.foregroundBase};
  background: ${CENTION_TOKENS.backgroundXlight};
  color: ${CENTION_TOKENS.textDark};
  font-family: inherit;
  line-height: ${CENTION_TOKENS.lineHeightCompact};

  &:hover {
    background: ${CENTION_TOKENS.backgroundLight};
    border-color: ${CENTION_TOKENS.foregroundDark};
  }

  &:active {
    transform: translateY(1px);
  }

  &.primary {
    background: ${CENTION_TOKENS.primary};
    color: ${CENTION_TOKENS.textXlight};
    border-color: ${CENTION_TOKENS.primary};

    &:hover {
      background: ${CENTION_TOKENS.primaryShade1};
      border-color: ${CENTION_TOKENS.primaryShade1};
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    pointer-events: none;
  }

  &.danger {
    background: ${CENTION_TOKENS.danger};
    color: ${CENTION_TOKENS.textXlight};
    border-color: ${CENTION_TOKENS.danger};

    &:hover {
      background: ${CENTION_TOKENS.dangerShade1};
      border-color: ${CENTION_TOKENS.dangerShade1};
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

// CENTION Workflow Node Component with execution status
const WorkflowNodeComponent = ({
  node,
  onSelect,
  onUpdate,
  onDelete,
  onEdit,
  onRemove,
  isSelected,
  onConnectionStart,
  onConnectionEnd,
  onContextMenu
}) => {
  const getNodeIcon = (type) => {
    switch (type) {
      case 'AGENT': return '🤖';
      case 'TOOL': return '🔧';
      case 'MODEL': return '🧠';
      case 'MEMORY': return '💾';
      default: return '📦';
    }
  };

  const getNodeStatus = (node) => {
    if (node.status === 'running') return 'running';
    if (node.status === 'success') return 'success';
    if (node.status === 'error') return 'error';
    if (node.status === 'waiting') return 'waiting';
    return '';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'waiting': return '⏳';
      case 'running': return '⚡';
      case 'success': return '✓';
      case 'error': return '✗';
      default: return null;
    }
  };

  return (
    <Draggable
      position={node.position}
      onStop={(e, data) => {
        onUpdate(node.id, { position: { x: data.x, y: data.y } });
      }}
      bounds="parent"
    >
      <WorkflowNode
        className={`${isSelected ? 'selected' : ''} ${getNodeStatus(node)}`}
        onMouseDown={(e) => {
          if (e.button === 0) { // Left click only
            e.stopPropagation();
            onSelect(node.id);
          }
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          onEdit(node);
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onContextMenu && onContextMenu(e, node);
        }}
      >
        {/* Input connection handle */}
        <ConnectionHandle
          className="input main"
          onMouseDown={(e) => {
            e.stopPropagation();
            onConnectionStart && onConnectionStart(node.id, { x: e.clientX, y: e.clientY }, false);
          }}
          onMouseUp={(e) => {
            e.stopPropagation();
            onConnectionEnd && onConnectionEnd(node.id, { x: e.clientX, y: e.clientY }, false);
          }}
        >
          <HandleDot />
        </ConnectionHandle>

        {/* Output connection handle */}
        <ConnectionHandle
          className="output main"
          onMouseDown={(e) => {
            e.stopPropagation();
            onConnectionStart && onConnectionStart(node.id, { x: e.clientX, y: e.clientY }, true);
          }}
          onMouseUp={(e) => {
            e.stopPropagation();
            onConnectionEnd && onConnectionEnd(node.id, { x: e.clientX, y: e.clientY }, true);
          }}
        >
          <HandleDot />
        </ConnectionHandle>

        {/* Node Status Indicator */}
        {node.status && (
          <NodeStatusIndicator className={node.status}>
            {getStatusIcon(node.status)}
          </NodeStatusIndicator>
        )}

        <NodeIcon>{getNodeIcon(node.type)}</NodeIcon>

        <NodeDescription>
          <NodeLabel>{node.title}</NodeLabel>
          {node.subtitle && <NodeSubtitle>{node.subtitle}</NodeSubtitle>}
        </NodeDescription>
      </WorkflowNode>
    </Draggable>
  );
};

// Main AgentBuilder Component
const AgentBuilder = ({
  agents = [],
  tools = [],
  sidebarLayout,
  workflows = [],
  areaList = [],
  agentList = [],
  loading = false,
  error = null,
  onCreateAgent,
  onUpdateAgent,
  onDeleteAgent,
  onCreateTool,
  onUpdateTool,
  onDeleteTool,
  loadAgents,
  loadTools,
  loadWorkflows,
  loadAgentAreas,
  loadAgentList,
  loadBuilderSidebarLayout,
  scheduleSaveBuilderSidebarLayout,
}) => {
  // console.log('AgentBuilder props:', { agents, tools, workflows, loading, error });
  // console.log('AgentBuilder areaList:', areaList);

  const dispatch = useDispatch();

  const handlePatchAgentFolder = (agentId, folderId) => {
    dispatch(patchAgentFolder(agentId, folderId));
  };

  // Use the workflow saving hook
  const {
    isNewWorkflow,
    isSaving,
    saveError,
    saveAsNewWorkflow,
    updateExistingWorkflow,
    saveCurrentWorkflow,
    startAutoSave,
    stopAutoSave,
    currentWorkflow
  } = useWorkflowSaving(areaList) || {};

  const [nodes, setNodes] = useState([]);
  const [connections, setConnections] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [showToolModal, setShowToolModal] = useState(false);
  const [modalData, setModalData] = useState(null); // Unified modal state: { type: 'agent'|'tool', id: 0|number, ...data }
  const [connectingNode, setConnectingNode] = useState(null);
  const [tempConnection, setTempConnection] = useState(null);
  const [hoveredConnection, setHoveredConnection] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [executionState, setExecutionState] = useState({});
  const [isExecuting, setIsExecuting] = useState(false);
  const [currentTheme, setCurrentTheme] = useState('light');
  const [activeTab, setActiveTab] = useState('editor');
  const [debugMode, setDebugMode] = useState(false);
  const [isSavingAgent, setIsSavingAgent] = useState(false);
  const [isDeletingAgent, setIsDeletingAgent] = useState(false);
  const [isSavingTool, setIsSavingTool] = useState(false);
  const [isDeletingTool, setIsDeletingTool] = useState(false);

  // Update debug mode when workflow settings change
  useEffect(() => {
    if (currentWorkflow?.settings?.enableDebugMode !== undefined) {
      setDebugMode(currentWorkflow.settings.enableDebugMode);
    }
  }, [currentWorkflow?.settings?.enableDebugMode]);

  // Apply theme to document root for CSS custom properties
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', currentTheme);
    return () => {
      document.documentElement.removeAttribute('data-theme');
    };
  }, [currentTheme]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Area dropdown state (handled internally by UnnestedAreaSingleSelect)

  // Workflow change handler
  const onWorkflowChange = (workflowId) => {
    // console.log('Workflow changed to:', workflowId);
    // Find the workflow and load it
    const workflow = workflows.find(w => w.id === workflowId);
    if (workflow) {
      // Load workflow data
      setNodes(workflow.nodes || []);
      setConnections(workflow.connections || []);
      // Additional workflow loading logic can be added here
    }
  };

  // Nodes change handler
  const onNodesChange = (newNodes) => {
    // console.log('Nodes changed:', newNodes);
    setNodes(newNodes);
  };

  // Connections change handler
  const onConnectionsChange = (newConnections) => {
    // console.log('Connections changed:', newConnections);
    setConnections(newConnections);
  };

  // Load data when component mounts 
  useEffect(() => {
    // console.log('AgentBuilder useEffect - loading data');
    if (loadAgents) loadAgents();
    if (loadTools) loadTools();
    if (loadWorkflows) loadWorkflows();
    if (loadAgentAreas) loadAgentAreas();
    if (loadAgentList) loadAgentList();
    if (loadBuilderSidebarLayout) loadBuilderSidebarLayout();
  }, []); // Empty dependency array to run only once on mount

  // Auto-save when nodes or connections change
  useEffect(() => {
    if ((nodes.length > 0 || connections.length > 0) && startAutoSave) {
      startAutoSave();
    }
    return () => stopAutoSave && stopAutoSave();
  }, [nodes, connections, startAutoSave, stopAutoSave]);

  // Update Redux state when nodes change
  useEffect(() => {
    // console.log('AgentBuilder useEffect - nodes changed, local nodes:', nodes);
    // Dispatch nodes to Redux state for workflow saving
    dispatch({ type: 'AGENT_BUILDER_SET_NODES', payload: nodes });
    // console.log('AgentBuilder useEffect - dispatched AGENT_BUILDER_SET_NODES with:', nodes);
    if (nodes.length > 0) {
      dispatch(setUnsavedChanges(true));
    }
  }, [nodes, dispatch]);

  // Update Redux state when connections change
  useEffect(() => {
    // console.log('AgentBuilder useEffect - connections changed, local connections:', connections);
    // Dispatch connections to Redux state for workflow saving
    dispatch({ type: 'AGENT_BUILDER_SET_CONNECTIONS', payload: connections });
    // console.log('AgentBuilder useEffect - dispatched AGENT_BUILDER_SET_CONNECTIONS with:', connections);
    if (connections.length > 0) {
      dispatch(setUnsavedChanges(true));
    }
  }, [connections, dispatch]);

  const handleCreateAgent = () => {
    console.log('Create new agent');
    setModalData({
      type: 'agent',
      id: 0, // 0 indicates create mode
      name: '',
      description: '',
      agentType: 'AUTO_ANSWER',
      areas: [],
      user_id: '',
      parentAgentId: ''
    });
    setShowAgentModal(true);
  };

  const handleEditAgent = (agent) => {
    console.log('Edit agent:', agent);

    // Map agent type from API format to UI format - only Router or empty string are valid
    const agentType = agent.agent_type || agent.type;
    let frontendType = '';
    if (agentType) {
      switch (agentType.toLowerCase()) {
        case 'router':
          frontendType = 'ROUTER';
          break;
        case '':
        case null:
        case undefined:
          frontendType = '';
          break;
        default:
          frontendType = '';
      }
    }

    const areaIds = agent.area_ids && Array.isArray(agent.area_ids) ? agent.area_ids : (agent.area_id != null || agent.area != null ? [agent.area_id ?? agent.area ?? agent.AreaID] : []);
    setModalData({
      type: 'agent',
      id: agent.id || agent.agent_id, // Existing ID indicates edit mode
      agent_id: agent.agent_id || agent.id,
      name: agent.name || agent.title || '',
      description: agent.description || '',
      agentType: frontendType,
      tools: agent.tools || [],
      user_id: agent.user_id || '',

      areas: areaIds.map((id) => (typeof id === 'number' ? id : parseInt(id, 10))).filter((n) => !isNaN(n)),
      parentAgentId: agent.parent_agent_id || agent.parentAgentId || ''
    });

    console.log('Setting modal data for edit');
    setShowAgentModal(true);
  };

  const handleSaveAgent = async () => {
    if (isSavingAgent) return; // Prevent multiple clicks

    try {
      setIsSavingAgent(true);

    // Validate required fields
    if (!modalData.name?.trim()) {
        alert(I('Agent name is required'));
        setIsSavingAgent(false);
        return;
      }
      if (!modalData.description?.trim()) {
        alert(I('Agent description is required'));
        setIsSavingAgent(false);
      return;
    }
    const areas = Array.isArray(modalData.areas) ? modalData.areas : (modalData.area != null && modalData.area !== '' ? [modalData.area] : []);
    if (!areas.length) {
        alert(I('At least one area must be selected'));
        setIsSavingAgent(false);
        return;
      }

    // Map frontend types to backend types - only Router or empty string are valid
    const getAgentType = (frontendType) => {
      switch(frontendType) {
        case 'ROUTER': return 'router';
        case '': return '';
        default: return '';
      }
    };

    const frontendType = modalData.agentType ?? modalData.agent_type ?? modalData.type;
    const agentType = getAgentType(frontendType);

    // Validate router agent constraints: router agents cannot have a parent
    if (agentType === 'router' && modalData.parentAgentId) {
      alert(I('Router agents cannot have a parent. Router agents must be at the top level of the hierarchy.'));
      setIsSavingAgent(false);
      return;
    }

      const agentData = {
        name: modalData.name.trim(),
        description: modalData.description.trim(),
        agent_type: agentType,
        areas: areas,
        user_id: modalData.user_id,
        parent_agent_id: modalData.parentAgentId || ''
      };

      const isEditing = modalData.id && modalData.id !== 0;

      if (isEditing) {
        // Update existing agent
        agentData.agent_id = modalData.agent_id || modalData.id;
        await onUpdateAgent(agentData);

        // Update visual node if it exists in canvas
        setNodes(prevNodes =>
          prevNodes.map(node =>
            (node.agent_id === agentData.agent_id || node.id === agentData.agent_id)
              ? { ...node, title: agentData.name, subtitle: agentData.description, type: modalData.agentType }
              : node
          )
        );
      } else {
        // Create new agent
        await onCreateAgent(agentData);
      }

      // Refresh the agents list
      loadAgents();

      // Show success toast notification
      if (dispatch) {
        dispatch({
          type: 'ADD_TOAST_ALERT',
          payload: {
            type: { class: 'success', icon: 'icon-check' },
            position: 'top-right',
            text: isEditing ? I('Agent updated successfully') : I('Agent created successfully')
          }
        });
      }

      // Close modal and reset state
      setShowAgentModal(false);
      setModalData(null);
    } catch (error) {
      console.error('Failed to save agent:', error);

      // Show error toast notification
      if (dispatch) {
        dispatch({
          type: 'ADD_TOAST_ALERT',
          payload: {
            type: { class: 'danger', icon: 'icon-alert' },
            position: 'top-right',
            text: I('Failed to save agent: ') + (error.message || 'Unknown error')
          }
        });
      }
    } finally {
      setIsSavingAgent(false);
    }
  };

  const handleNodeSelect = (nodeId) => {
    console.log('handleNodeSelect called with:', nodeId);
    setSelectedNode(nodeId);
  };

  const handleNodeEdit = (node) => {
    console.log('handleNodeEdit called with:', node);

    // Check if this is a tool node or agent node
    const nodeType = node.type?.toUpperCase();
    const isTool = nodeType === 'TOOL';

    if (isTool) {
      // Handle tool editing
      const toolData = node.data || {};
      console.log('Editing tool from canvas:', toolData);

      setModalData({
        type: 'tool',
        id: toolData.id || toolData.tool_id || 0,
        toolName: toolData.toolName || toolData.name || node.title || '',
        toolDescription: toolData.toolDescription || toolData.description || node.description || '',
        selectedArea: toolData.area_id || toolData.area || '',
        resp_text: toolData.resp_text || '',
        ...toolData
      });
      setShowToolModal(true);
    } else {
      // Handle agent editing
      const agentData = node.data || {};
      console.log('Editing agent from canvas:', agentData);

      // Map agent type from API format to UI format
      const agentType = agentData.agent_type || agentData.type || node.subtitle?.toLowerCase();
      let frontendType = '';
      if (agentType) {
        switch (agentType.toLowerCase()) {
          case 'router':
            frontendType = 'ROUTER';
            break;
          case '':
          case null:
          case undefined:
            frontendType = '';
            break;
          default:
            frontendType = '';
        }
      }

      const nodeAreaIds = agentData.area_ids && Array.isArray(agentData.area_ids) ? agentData.area_ids : (agentData.area_id != null || agentData.area != null ? [agentData.area_id ?? agentData.area ?? agentData.AreaID] : []);
      setModalData({
        type: 'agent',
        id: agentData.id || agentData.agent_id,
        agent_id: agentData.agent_id || agentData.id || '',
        name: agentData.name || agentData.agent_name || node.title || '',
        description: agentData.description || node.description || '',
        agentType: frontendType,
        tools: agentData.tools || node.tools || [],
        areas: nodeAreaIds.map((id) => (typeof id === 'number' ? id : parseInt(id, 10))).filter((n) => !isNaN(n)),
        user_id: agentData.user_id || '',
        parentAgentId: agentData.parent_agent_id || agentData.parentAgentId || ''
      });

      console.log('Setting modal data for agent');
      setShowAgentModal(true);
    }
  };

  const handleNodeUpdate = (nodeId, updates) => {
    console.log('handleNodeUpdate called with:', nodeId, updates);
    setNodes(prevNodes =>
      prevNodes.map(node => {
        if (node.id === nodeId) {
          // Handle position updates
          if (updates.position) {
            return {
              ...node,
              position: updates.position,
            };
          }
          // Handle tool assignments
          if (updates.type === 'TOOL') {
            return {
              ...node,
              tools: [...(node.tools || []), updates.tool],
            };
          } else if (updates.type === 'MODEL') {
            return {
              ...node,
              model: updates.model,
            };
          } else if (updates.type === 'MEMORY') {
            return {
              ...node,
              memory: updates.memory,
            };
          }
        }
        return node;
      })
    );

    // Update connection positions when node moves
    if (updates.position) {
      setConnections(prevConnections =>
        prevConnections.map(conn => {
          const nodeWidth = DEFAULT_NODE_SIZE[0];
          const nodeHeight = DEFAULT_NODE_SIZE[1];

          if (conn.source === nodeId) {
            // Calculate source handle position (right side of node)
            return {
              ...conn,
              sourceX: updates.position.x + nodeWidth,
              sourceY: updates.position.y + nodeHeight / 2,
            };
          }
          if (conn.target === nodeId) {
            // Calculate target handle position (left side of node)
            return {
              ...conn,
              targetX: updates.position.x,
              targetY: updates.position.y + nodeHeight / 2,
            };
          }
          return conn;
        })
      );
    }
  };

  const handleNodeDelete = () => {
    if (selectedNode) {
      setNodes(prevNodes => prevNodes.filter(node => node.id !== selectedNode));
      setConnections(prevConnections =>
        prevConnections.filter(conn =>
          conn.source !== selectedNode && conn.target !== selectedNode
        )
      );
      setSelectedNode(null);
    }
  };

  const handleNodeRemove = () => {
    if (selectedNode) {
      // Just remove from canvas, don't delete permanently
      setNodes(prevNodes => prevNodes.filter(node => node.id !== selectedNode));
      setConnections(prevConnections =>
        prevConnections.filter(conn =>
          conn.source !== selectedNode && conn.target !== selectedNode
        )
      );
      setSelectedNode(null);
    }
  };

  const handleConnectionDelete = (connectionId) => {
    console.log('Deleting connection:', connectionId);
    setConnections(prevConnections =>
      prevConnections.filter(conn => conn.id !== connectionId)
    );
  };

  // Real Tool Testing Execution Flow
  const simulateExecution = async () => {
    if (isExecuting || nodes.length === 0) return;

    setIsExecuting(true);
    setExecutionState({});

    // Find the starting node (first node or one without input connections)
    const startingNodes = nodes.filter(node =>
      !connections.some(conn => conn.target === node.id)
    );

    if (startingNodes.length === 0 && nodes.length > 0) {
      // If no clear starting node, use the first one
      startingNodes.push(nodes[0]);
    }

    // Execute nodes in sequence with real testing
    for (const startNode of startingNodes) {
      await executeNodeFlow(startNode.id, new Set());
    }

    setIsExecuting(false);
  };

  const executeNodeFlow = async (nodeId, visited) => {
    if (visited.has(nodeId)) return; // Prevent infinite loops
    visited.add(nodeId);

    const currentNode = nodes.find(node => node.id === nodeId);
    if (!currentNode) return;

    // Set node to waiting
    setNodes(prev => prev.map(node =>
      node.id === nodeId ? { ...node, status: 'waiting' } : node
    ));

    await delay(500);

    // Set node to running
    setNodes(prev => prev.map(node =>
      node.id === nodeId ? { ...node, status: 'running' } : node
    ));

    // Find outgoing connections
    const outgoingConnections = connections.filter(conn => conn.source === nodeId);

    // Set connections to running
    outgoingConnections.forEach(conn => {
      setExecutionState(prev => ({
        ...prev,
        [conn.id]: { status: 'running' }
      }));
    });

    // Test the actual tool/node
    let success = true;
    let errorMessage = '';
    let errorDetails = null;

    try {
      success = await testNodeTool(currentNode);
    } catch (error) {
      success = false;
      errorMessage = error.message;
      errorDetails = error.details || error; // Store full error details for debug mode
      console.error(`Node ${nodeId} test failed:`, error);
    }

    const finalStatus = success ? 'success' : 'error';

    // Set node final status
    setNodes(prev => prev.map(node =>
      node.id === nodeId ? {
        ...node,
        status: finalStatus,
        error: errorMessage || undefined,
        errorDetails: errorDetails // Store for debug display
      } : node
    ));

    // Set connections final status
    outgoingConnections.forEach(conn => {
      setExecutionState(prev => ({
        ...prev,
        [conn.id]: { status: finalStatus }
      }));
    });

    // If successful, execute connected nodes
    if (success) {
      await delay(300); // Brief pause before next node

      for (const conn of outgoingConnections) {
        await executeNodeFlow(conn.target, visited);
      }
    }
  };

  // Test individual node/tool functionality
  const testNodeTool = async (node) => {
    // console.log('Testing node:', node);
    // console.log('Node type:', node.type);
    // console.log('Node data:', node.data);

    // For workflow nodes, try to find any tool configuration
    const nodeData = node.data || {};
    const nodeType = node.type || '';
    const nodeName = nodeData.name || nodeData.label || nodeType;

    // console.log(`Testing node "${nodeName}" of type "${nodeType}"`);

    // Try to extract tool information from various possible locations
    const toolName = nodeData.toolName || nodeData.tool_name || nodeData.name;
    const toolType = nodeData.toolType || nodeData.tool_type || nodeType;
    const agentId = nodeData.agentId || nodeData.agent_id;

    // If this node represents a tool, test it via the backend
    if (toolName && toolType) {
      console.log(`Testing tool: ${toolName} (${toolType})`);
      return await testWorkflowTool(toolName, toolType, nodeData);
    }

    // If node represents an agent, test its tools
    if (agentId) {
      console.log(`Testing agent tools for: ${agentId}`);
      return await testAgentTools(agentId);
    }

    // Test based on node type with real API calls
    switch (nodeType.toLowerCase()) {
      case 'http':
      case 'api':
      case 'webhook':
      case 'httprequest':
        console.log('Testing HTTP node with real API call');
        return await testHttpNodeReal(node);

      case 'database':
      case 'db':
      case 'postgres':
      case 'mysql':
        console.log('Testing database node with real API call');
        return await testDatabaseNodeReal(node);

      case 'email':
      case 'smtp':
      case 'mail':
        console.log('Testing email node with real API call');
        return await testEmailNodeReal(node);

      case 'file':
      case 'storage':
      case 'filesystem':
        console.log('Testing file node with real API call');
        return await testFileNodeReal(node);

      case 'agent':
        console.log('Testing agent node');
        if (agentId) {
          return await testAgentTools(agentId);
        }
        return true; // Agents are always valid

      default:
        // For any other node type, try to test it as a generic tool
        console.log(`Testing generic node type: ${nodeType}`);
        return await testGenericNodeReal(node);
    }
  };

  // Test workflow tool via backend API
  const testWorkflowTool = async (toolName, toolType, nodeData) => {
    try {
      console.log(`Making API call to test tool: ${toolName} (${toolType})`);

      // Create a mock agent with this tool to test it
      const testPayload = {
        tool_name: toolName,
        tool_type: toolType,
        configuration: JSON.stringify(nodeData)
      };

      const response = await fetch(CheckEndpoint(AgentPrefix + 'test-workflow-tool'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(testPayload)
      });

      if (!response.ok) {
        const errorDetails = {
          error: `API call failed with status ${response.status}`,
          tool_name: toolName,
          tool_type: toolType,
          status_code: response.status
        };
        const error = new Error(`Tool test failed: ${response.status}`);
        error.details = errorDetails;
        throw error;
      }

      const result = await response.json();
      console.log('Tool test result:', result);

      if (result.status !== 'healthy') {
        const error = new Error(result.error || 'Tool test failed');
        error.details = result;
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Workflow tool test failed:', error);
      if (error.details) {
        throw error; // Re-throw with details
      }
      const newError = new Error('Workflow tool test failed: ' + error.message);
      newError.details = { error: error.message, tool_name: toolName, tool_type: toolType };
      throw newError;
    }
  };

  // Test agent tools
  const testAgentTools = async (agentId) => {
    try {
      const response = await fetch(`/${agent_hierarchy}/${agentId}/test/tools`);

      if (!response.ok) {
        throw new Error(`Agent test failed: ${response.status}`);
      }

      const result = await response.json();
      return result.success && result.overall_health > 0.5; // Consider 50%+ health as success
    } catch (error) {
      console.error('Agent tools test failed:', error);
      return false;
    }
  };

  // Test HTTP node via backend API
  const testHttpNodeReal = async (node) => {
    try {
      console.log('Making API call to test HTTP node');

      const toolConfig = {
        url: node.data?.url || node.data?.endpoint || 'https://httpbin.org/get',
        headers: node.data?.headers || {},
        method: node.data?.method || 'GET'
      };

      const response = await fetch('/test-http-tool', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(toolConfig)
      });

      if (!response.ok) {
        const errorDetails = {
          error: `HTTP test API call failed with status ${response.status}`,
          status_code: response.status,
          test_type: 'http'
        };
        const error = new Error(`HTTP test API call failed: ${response.status}`);
        error.details = errorDetails;
        throw error;
      }

      const result = await response.json();
      console.log('HTTP test result:', result);

      if (result.status !== 'healthy') {
        const error = new Error(result.error || 'HTTP test failed');
        error.details = result;
        throw error;
      }

      return true;
    } catch (error) {
      console.error('HTTP node test failed:', error);
      if (error.details) {
        throw error;
      }
      const newError = new Error('HTTP node test failed: ' + error.message);
      newError.details = { error: error.message, test_type: 'http' };
      throw newError;
    }
  };

  // Test Database node via backend API
  const testDatabaseNodeReal = async (node) => {
    try {
      console.log('Making API call to test database node');

      const toolConfig = {
        host: node.data?.host || node.data?.server || 'localhost',
        port: node.data?.port || '5432',
        database: node.data?.database || node.data?.db || 'test'
      };

      const response = await fetch('/test-database-tool', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(toolConfig)
      });

      if (!response.ok) {
        const errorDetails = {
          error: `Database test API call failed with status ${response.status}`,
          status_code: response.status,
          test_type: 'database'
        };
        const error = new Error(`Database test API call failed: ${response.status}`);
        error.details = errorDetails;
        throw error;
      }

      const result = await response.json();
      console.log('Database test result:', result);

      if (result.status !== 'healthy') {
        const error = new Error(result.error || 'Database test failed');
        error.details = result;
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Database node test failed:', error);
      if (error.details) {
        throw error;
      }
      const newError = new Error('Database node test failed: ' + error.message);
      newError.details = { error: error.message, test_type: 'database' };
      throw newError;
    }
  };

  // Test Email node via backend API
  const testEmailNodeReal = async (node) => {
    try {
      console.log('Making API call to test email node');

      const toolConfig = {
        smtp_host: node.data?.smtpHost || node.data?.host || 'smtp.gmail.com',
        smtp_port: node.data?.smtpPort || node.data?.port || '587'
      };

      const response = await fetch('/test-email-tool', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(toolConfig)
      });

      if (!response.ok) {
        console.error('Email test API call failed:', response.status);
        return false;
      }

      const result = await response.json();
      console.log('Email test result:', result);
      return result.status === 'healthy';
    } catch (error) {
      console.error('Email node test failed:', error);
      return false;
    }
  };

  // Test File node via backend API
  const testFileNodeReal = async (node) => {
    try {
      console.log('Making API call to test file node');

      const toolConfig = {
        path: node.data?.path || node.data?.filePath || '/tmp'
      };

      const response = await fetch('/test-file-tool', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(toolConfig)
      });

      if (!response.ok) {
        console.error('File test API call failed:', response.status);
        return false;
      }

      const result = await response.json();
      console.log('File test result:', result);
      return result.status === 'healthy';
    } catch (error) {
      console.error('File node test failed:', error);
      return false;
    }
  };

  // Test generic node via backend API
  const testGenericNodeReal = async (node) => {
    try {
      console.log('Making API call to test generic node');

      const toolConfig = {
        node_type: node.type,
        configuration: JSON.stringify(node.data || {})
      };

      const response = await fetch('/test-generic-tool', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(toolConfig)
      });

      if (!response.ok) {
        console.error('Generic test API call failed:', response.status);
        return false;
      }

      const result = await response.json();
      console.log('Generic test result:', result);
      return result.status === 'healthy';
    } catch (error) {
      console.error('Generic node test failed:', error);
      return false;
    }
  };

  const resetExecution = () => {
    setNodes(prev => prev.map(node => ({ ...node, status: undefined })));
    setExecutionState({});
    setIsExecuting(false);
  };

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const handleContextMenu = (event, node) => {
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      nodeId: node.id
    });
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedNode) {
          handleNodeDelete();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedNode]);

  // Close context menu when clicking elsewhere
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenu) {
        // Check if the click is outside the context menu
        const contextMenuElement = document.querySelector('.agent-builder-context-menu');
        if (contextMenuElement && !contextMenuElement.contains(event.target)) {
          handleContextMenuClose();
        }
      }
    };

    // Use mousedown instead of click to catch the event before menu items process it
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [contextMenu]);

  const handleConnectionStart = (nodeId, position, isOutput) => {
    // console.log('Connection start:', nodeId, position, isOutput);

    // Calculate actual handle position based on node position
    const node = nodes.find(n => n.id === nodeId);
    if (node) {
      const nodeWidth = DEFAULT_NODE_SIZE[0];
      const nodeHeight = DEFAULT_NODE_SIZE[1];

      const handlePosition = {
        x: isOutput
          ? node.position.x + nodeWidth  // Output handle on right side
          : node.position.x,              // Input handle on left side
        y: node.position.y + nodeHeight / 2  // Center vertically
      };

      setConnectingNode({
        id: nodeId,
        isOutput,
        position: handlePosition,
        nodePosition: node.position
      });
    }
  };

  const handleConnectionEnd = (nodeId, position, isOutput) => {
    // console.log('Connection end:', nodeId, position, isOutput);
    if (connectingNode && connectingNode.id !== nodeId) {
      // Calculate target handle position
      const targetNode = nodes.find(n => n.id === nodeId);
      if (targetNode) {
        const nodeWidth = DEFAULT_NODE_SIZE[0];
        const nodeHeight = DEFAULT_NODE_SIZE[1];

        const targetHandlePosition = {
          x: isOutput
            ? targetNode.position.x + nodeWidth  // Output handle on right side
            : targetNode.position.x,              // Input handle on left side
          y: targetNode.position.y + nodeHeight / 2  // Center vertically
        };

        // Create connection with proper handle positions
        const newConnection = {
          id: `conn_${Date.now()}`,
          source: connectingNode.isOutput ? connectingNode.id : nodeId,
          target: connectingNode.isOutput ? nodeId : connectingNode.id,
          sourceX: connectingNode.isOutput ? connectingNode.position.x : targetHandlePosition.x,
          sourceY: connectingNode.isOutput ? connectingNode.position.y : targetHandlePosition.y,
          targetX: connectingNode.isOutput ? targetHandlePosition.x : connectingNode.position.x,
          targetY: connectingNode.isOutput ? targetHandlePosition.y : connectingNode.position.y,
          sourceHandle: connectingNode.isOutput ? 'output' : 'input',
          targetHandle: connectingNode.isOutput ? 'input' : 'output',
          type: 'main'
        };
        // console.log('Creating new connection:', newConnection);
        // console.log('Current local connections before adding:', connections);
        setConnections(prev => {
          const updatedConnections = [...prev, newConnection];
          // console.log('Canvas now has', updatedConnections.length, 'connections');
          // console.log('Updated connections array:', updatedConnections);
          return updatedConnections;
        });
      }
    }
    setConnectingNode(null);
    setTempConnection(null);
  };

  const handleCanvasMouseMove = (e) => {
    if (connectingNode) {
      // Get canvas bounds for proper coordinate calculation
      const canvas = e.currentTarget.querySelector('[data-canvas="true"]') || e.currentTarget;
      const canvasRect = canvas.getBoundingClientRect();

      const targetX = e.clientX - canvasRect.left;
      const targetY = e.clientY - canvasRect.top;

      setTempConnection({
        sourceX: connectingNode.position.x,
        sourceY: connectingNode.position.y,
        targetX: targetX,
        targetY: targetY,
        type: 'main'
      });
    }
  };

  const handleAddNode = (item, type) => {
    // console.log('Adding node to canvas:', item.name || item.title || item.agent_name || item.agent_id, 'Type:', type);
    console.log('Current local nodes before adding:', nodes);
    const newNode = {
      id: `${type}_${Date.now()}`,
      title: item.name || item.title || item.agent_name || item.agent_id,
      subtitle: item.type || type.toLowerCase(),
      description: item.description || '',
      type: type.toUpperCase(),
      position: { x: 150 + Math.random() * 200, y: 150 + Math.random() * 200 },
      data: item,
    };
    console.log('New node created:', newNode);
    setNodes(prev => {
      const updatedNodes = [...prev, newNode];
      console.log('Canvas now has', updatedNodes.length, 'nodes');
      console.log('Updated nodes array:', updatedNodes);
      return updatedNodes;
    });
  };

  const handleDeleteAgent = async (agentId) => {
    if (isDeletingAgent) return; // Prevent multiple clicks

    console.log('Delete agent:', agentId);
    if (!window.confirm(I('Are you sure you want to delete this agent? This action cannot be undone.'))) {
      return;
    }

    try {
      setIsDeletingAgent(true);
      if (onDeleteAgent) {
        await onDeleteAgent(agentId);

        // Show success toast notification
        if (dispatch) {
          dispatch({
            type: 'ADD_TOAST_ALERT',
            payload: {
              type: { class: 'success', icon: 'icon-check' },
              position: 'top-right',
              text: I('Agent deleted successfully')
            }
          });
        }
      }
    } catch (error) {
      console.error('Error deleting agent:', error);

      // Show error toast notification
      if (dispatch) {
        dispatch({
          type: 'ADD_TOAST_ALERT',
          payload: {
            type: { class: 'danger', icon: 'icon-alert' },
            position: 'top-right',
            text: I('Failed to delete agent: ') + (error.message || 'Unknown error')
          }
        });
      }
    } finally {
      setIsDeletingAgent(false);
    }
  };

  const handleCreateTool = () => {
    console.log('Create new tool');
    setModalData({
      type: 'tool',
      id: 0, // 0 indicates create mode
      toolName: '',
      toolDescription: '',
      selectedArea: '',
      resp_text: '',
      parameters: []
    });
    setShowToolModal(true);
  };

  const handleEditTool = (tool) => {
    console.log('Edit tool:', tool);
    // Use raw backend tool for area and for form originalData (backend may send areas as array)
    const rawTool = tool.originalData || tool;
    const areaFromRaw =
      rawTool.areas && Array.isArray(rawTool.areas)
        ? rawTool.areas.length === 1
          ? String(rawTool.areas[0])
          : rawTool.areas.map((a) => String(a)).join(',')
        : rawTool.area_id != null
          ? String(rawTool.area_id)
          : rawTool.area != null
            ? (typeof rawTool.area === 'string' ? rawTool.area : String(rawTool.area))
            : '';
    const selectedArea =
      tool.area_id != null
        ? String(tool.area_id)
        : tool.area != null
          ? (typeof tool.area === 'string' ? tool.area : String(tool.area))
          : areaFromRaw;
    setModalData({
      type: 'tool',
      id: tool.id || tool.tool_id,
      toolName: tool.toolName || tool.name || '',
      toolDescription: tool.toolDescription || tool.description || '',
      selectedArea: selectedArea || '',
      resp_text: tool.resp_text || '',
      parameters: tool.parameters || [],
      originalData: rawTool,
    });
    setShowToolModal(true);
  };

  const handleDeleteTool = async (toolId) => {
    if (isDeletingTool) return; // Prevent multiple clicks

    console.log('Delete tool:', toolId);
    if (!window.confirm(I('Are you sure you want to delete this tool? This action cannot be undone.'))) {
      return;
    }

    try {
      setIsDeletingTool(true);
      if (onDeleteTool) {
        await onDeleteTool(toolId);

        // Show success toast notification
        if (dispatch) {
          dispatch({
            type: 'ADD_TOAST_ALERT',
            payload: {
              type: { class: 'success', icon: 'icon-check' },
              position: 'top-right',
              text: I('Tool deleted successfully')
            }
          });
        }
      }
    } catch (error) {
      console.error('Error deleting tool:', error);

      // Show error toast notification
      if (dispatch) {
        dispatch({
          type: 'ADD_TOAST_ALERT',
          payload: {
            type: { class: 'danger', icon: 'icon-alert' },
            position: 'top-right',
            text: I('Failed to delete tool: ') + (error.message || 'Unknown error')
          }
        });
      }
    } finally {
      setIsDeletingTool(false);
    }
  };

  const handleToolFormChange = (formData) => {
    setModalData((prev) => {
      if (!prev || prev.type !== 'tool') return prev;
      return { ...prev, ...formData };
    });
  };

  const handleAgentFormChange = (formData) => {
    setModalData((prev) => {
      if (!prev || prev.type !== 'agent') return prev;
      const next = {
        ...prev,
        name: formData.name,
        description: formData.description,
        agentType: formData.agent_type,
        agent_type: formData.agent_type,
        areas: Array.isArray(formData.areas) ? formData.areas : [],
        area: (Array.isArray(formData.areas) && formData.areas[0] != null) ? formData.areas[0] : formData.area,
        user_id: formData.user_id,
        parentAgentId: formData.parentAgentId || ''
      };
      console.log('[AgentBuilder] handleAgentFormChange next modalData', { areas: next.areas, user_id: next.user_id });
      return next;
    });
  };



  const handleSaveTool = async () => {
    if (isSavingTool) return; // Prevent multiple clicks

    try {
      setIsSavingTool(true);

      // Validate tool data before saving
      const validationErrors = [];

      // Check required fields
      if (!modalData.toolName || modalData.toolName.trim() === '') {
        validationErrors.push('Tool name is required');
      }
      if (!modalData.toolDescription || modalData.toolDescription.trim() === '') {
        validationErrors.push('Tool description is required');
      }
      if (!modalData.selectedArea || modalData.selectedArea.trim() === '') {
        validationErrors.push('Area selection is required');
      }

      // Check for duplicate parameter names
      if (modalData.parameters && Array.isArray(modalData.parameters)) {
        const parameterNames = modalData.parameters.map(param => param.name?.toLowerCase().trim()).filter(name => name);
        const duplicateNames = parameterNames.filter((name, index) => parameterNames.indexOf(name) !== index);

        if (duplicateNames.length > 0) {
          validationErrors.push(`Duplicate parameter names found: ${[...new Set(duplicateNames)].join(', ')}`);
        }

        // TEMPORARILY COMMENTED OUT - Check that internal parameters have enum values
        // TODO: Find better way to handle this validation
        // modalData.parameters.forEach((param, index) => {
        //   if (param.internal && (!param.enum || param.enum.trim() === '')) {
        //     validationErrors.push(`Parameter "${param.name}" (index ${index + 1}) is marked as internal but has no enum value configured.`);
        //   }
        // });
      }

      // If there are validation errors, show them and don't save
      if (validationErrors.length > 0) {
        if (dispatch) {
          dispatch({
            type: 'ADD_TOAST_ALERT',
            payload: {
              type: { class: 'danger', icon: 'icon-alert' },
              position: 'top-right',
              text: `Validation failed: ${validationErrors.join('; ')}`
            }
          });
        }
        setIsSavingTool(false);
        return;
      }

      const toolData = {
        ...modalData,
        id: modalData.id || modalData.tool_id,
      };

      const isEditing = modalData.id && modalData.id !== 0;

      if (isEditing) {
        // Update existing tool
        if (onUpdateTool) {
          await onUpdateTool(toolData);
        }
      } else {
        // Create new tool
        if (onCreateTool) {
          await onCreateTool(toolData);
        }
      }

      // Small delay to ensure backend has processed the save
      await new Promise(resolve => setTimeout(resolve, 500));

      // Refresh tools list first to get updated data
      if (loadTools) {
        await loadTools();
      }

      // Show success toast notification
      if (dispatch) {
        dispatch({
          type: 'ADD_TOAST_ALERT',
          payload: {
            type: { class: 'success', icon: 'icon-check' },
            position: 'top-right',
            text: isEditing ? I('Tool updated successfully') : I('Tool created successfully')
          }
        });
      }

      setShowToolModal(false);
      setModalData(null);
    } catch (error) {
      console.error('Error saving tool:', error);

      // Show error toast notification
      if (dispatch) {
        dispatch({
          type: 'ADD_TOAST_ALERT',
          payload: {
            type: { class: 'danger', icon: 'icon-alert' },
            position: 'top-right',
            text: I('Failed to save tool: ') + (error.message || 'Unknown error')
          }
        });
      }
    } finally {
      setIsSavingTool(false);
    }
  };

  console.log('Rendering agents:', agents);
  console.log('Rendering tools:', tools);

  return (
    <CentionAppContainer data-theme={currentTheme}>
      {/* CENTION Header */}
      <CentionHeader
        workflowName={currentWorkflow?.name || 'My workflow'}
        workflowActive={currentWorkflow?.active || true}
        onWorkflowNameChange={(name) => {
          // Handle workflow name change
          console.log('Workflow name changed:', name);
        }}
        onThemeToggle={() => setCurrentTheme(prev => prev === 'light' ? 'dark' : 'light')}
        currentTheme={currentTheme}
        activeTab={activeTab}
        onTabChange={(tab) => setActiveTab(tab)}
        workflows={workflows}
        onWorkflowChange={onWorkflowChange}
        onNodesChange={onNodesChange}
        onConnectionsChange={onConnectionsChange}
        areaList={areaList}
        currentWorkflow={currentWorkflow}
      />

      <CentionMainLayout>
        {/* CENTION Sidebar */}
        <CentionSidebar
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          agents={agents}
          tools={tools}
          sidebarLayout={sidebarLayout}
          onSidebarLayoutChange={scheduleSaveBuilderSidebarLayout}
          onPatchAgentFolder={handlePatchAgentFolder}
          onNodeClick={handleAddNode}
          onCreateAgent={handleCreateAgent}
          onCreateTool={handleCreateTool}
          onEditAgent={handleEditAgent}
          onDeleteAgent={handleDeleteAgent}
          onEditTool={handleEditTool}
          onDeleteTool={handleDeleteTool}
          isDeletingAgent={isDeletingAgent}
          isDeletingTool={isDeletingTool}
        />

        {/* Canvas Container */}
        <CentionCanvasContainer onMouseMove={handleCanvasMouseMove}>
          {activeTab === 'editor' ? (
            <>
              <CanvasBackground />

              {/* Help message when no nodes */}
              {nodes.length === 0 && (
                <CentionEmptyState>
                  <div>👆 Click on items in the sidebar to add them to your workflow</div>
                  <div style={{ fontSize: '14px', marginTop: '10px', opacity: 0.7 }}>
                    Then drag between connection points (dots) to create connections
                  </div>
                </CentionEmptyState>
              )}

              {/* Workflow Nodes */}
              {nodes.map((node) => (
                <WorkflowNodeComponent
                  key={node.id}
                  node={node}
                  isSelected={selectedNode === node.id}
                  onSelect={handleNodeSelect}
                  onUpdate={handleNodeUpdate}
                  onDelete={handleNodeDelete}
                  onEdit={handleNodeEdit}
                  onRemove={handleNodeRemove}
                  onConnectionStart={handleConnectionStart}
                  onConnectionEnd={handleConnectionEnd}
                  onContextMenu={handleContextMenu}
                />
              ))}

              {/* Debug Error Displays */}
              {debugMode && nodes.filter(node => node.status === 'error' && node.errorDetails).map(node => (
                <div
                  key={`debug-${node.id}`}
                  style={{
                    position: 'absolute',
                    left: node.position.x,
                    top: node.position.y + 120, // Position below the node
                    width: '400px',
                    zIndex: 1000,
                    pointerEvents: 'auto'
                  }}
                >
                  <DebugErrorView
                    error={node.errorDetails}
                    nodeName={node.title}
                    debugMode={true}
                  />
                </div>
              ))}

              {/* Connection Lines */}
              <ConnectionLine>
                <ConnectionRenderer
                  connections={connections}
                  tempConnection={tempConnection}
                  onDelete={handleConnectionDelete}
                  onHover={setHoveredConnection}
                  hoveredConnection={hoveredConnection}
                  executionState={executionState}
                />
              </ConnectionLine>
            </>
          ) : activeTab === 'settings' ? (
            <WorkflowSettings
              currentWorkflow={currentWorkflow}
              areaList={areaList}
              onWorkflowUpdate={(updatedWorkflow) => {
                dispatch(setCurrentWorkflow(updatedWorkflow));
                onWorkflowChange?.(updatedWorkflow);
              }}
              onSaveWorkflow={saveCurrentWorkflow}
            />
          ) : null}
        </CentionCanvasContainer>
      </CentionMainLayout>

      {/* Bottom Toolbar - CENTION Style (Fixed Position) */}
      <BottomToolbar
        isExecuting={isExecuting}
        executionStatus={isExecuting ? 'running' : (nodes.some(n => n.status === 'success') ? 'success' : (nodes.some(n => n.status === 'error') ? 'error' : 'idle'))}
        nodes={nodes}
        onStartExecution={simulateExecution}
        onStopExecution={() => setIsExecuting(false)}
        onResetExecution={resetExecution}
        onClearCanvas={() => {
          setNodes([]);
          setConnections([]);
          setExecutionState({});
          setSelectedNode(null);
        }}
      />

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          style={{
            left: contextMenu.x,
            top: contextMenu.y
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <ContextMenuItem onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const node = nodes.find(n => n.id === contextMenu.nodeId);
            if (node) {
              // Small delay to ensure the click is processed
              setTimeout(() => {
                handleNodeEdit(node);
                handleContextMenuClose();
              }, 10);
            }
          }}>
            {I('Edit')}
          </ContextMenuItem>
          <ContextMenuItem onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const nodeId = contextMenu.nodeId;
            setSelectedNode(nodeId);
            // Remove the node directly without relying on selectedNode state
            setNodes(prevNodes => prevNodes.filter(node => node.id !== nodeId));
            setConnections(prevConnections =>
              prevConnections.filter(conn =>
                conn.source !== nodeId && conn.target !== nodeId
              )
            );
            handleContextMenuClose();
          }}>
            {I('Remove from Canvas')}
          </ContextMenuItem>
        </ContextMenu>
      )}

      {/* Unified Agent Modal */}
      {showAgentModal && modalData && (
        <Modal onClick={() => setShowAgentModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{modalData.id && modalData.id !== 0 ? I('Edit Agent') : I('Create New Agent')}</ModalTitle>

            <AgentForm
              agent={modalData}
              agents={agents}
              areaList={areaList}
              agentList={agentList}
              onChange={handleAgentFormChange}
            />

            <ModalActions>
              <Button
                onClick={() => {
                  if (!isSavingAgent) {
                    setShowAgentModal(false);
                    setModalData(null);
                  }
                }}
                disabled={isSavingAgent}
              >
                {I('Cancel')}
              </Button>
              <Button
                className="primary"
                onClick={handleSaveAgent}
                disabled={isSavingAgent}
              >
                {isSavingAgent ? (
                  <>
                    <i className="fa fa-spinner fa-spin" style={{ marginRight: '8px' }} aria-hidden="true" />
                    {modalData.id && modalData.id !== 0 ? I('Updating...') : I('Creating...')}
                  </>
                ) : (
                  modalData.id && modalData.id !== 0 ? I('Update Agent') : I('Create Agent')
                )}
              </Button>
            </ModalActions>
          </ModalContent>
        </Modal>
      )}

      {/* Tool Modal */}
      {showToolModal && modalData && (
        <Modal onClick={() => setShowToolModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{modalData.id && modalData.id !== 0 ? I('Edit Tool') : I('Create New Tool')}</ModalTitle>

            <ToolForm
              tool={modalData}
              areaList={areaList}
              onChange={handleToolFormChange}
            />

            <ModalActions>
              <Button
                onClick={() => {
                  if (!isSavingTool) {
                    setShowToolModal(false);
                    setModalData(null);
                  }
                }}
                disabled={isSavingTool}
              >
                {I('Cancel')}
              </Button>
              <Button
                className="primary"
                onClick={handleSaveTool}
                disabled={isSavingTool}
              >
                {isSavingTool ? (
                  <>
                    <i className="fa fa-spinner fa-spin" style={{ marginRight: '8px' }} aria-hidden="true" />
                    {modalData.id && modalData.id !== 0 ? I('Saving...') : I('Creating...')}
                  </>
                ) : (
                  modalData.id && modalData.id !== 0 ? I('Save Changes') : I('Create Tool')
                )}
              </Button>
            </ModalActions>
          </ModalContent>
        </Modal>
      )}
    </CentionAppContainer>
  );
};

// CENTION Layout Components
const CentionAppContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: var(--cention-color-background-base);
  font-family: var(--cention-font-family-base);
`;

const CentionMainLayout = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const CentionCanvasContainer = styled.div`
  position: relative;
  flex: 1;
  background-color: var(--cention-color-background-light);
  background-image: radial-gradient(circle at 1px 1px, var(--cention-color-border-light) 1px, transparent 0);
  background-size: 20px 20px;
  overflow: hidden;
`;

const CentionEmptyState = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: var(--cention-color-text-light);
  font-size: 16px;
  pointer-events: none;
  z-index: 1;

  div:first-child {
    font-size: 18px;
    font-weight: var(--cention-font-weight-medium);
    color: var(--cention-color-text-base);
  }
`;

export default AgentBuilder;